import type { KeyboardEvent } from 'react';

interface ApplicationsNudgeCardProps {
  /** Number of new applications waiting across open campaigns. */
  count: number;
  /** Where the interest is coming from — a campaign name or a summary line. */
  hint: string;
  /** CTA handler — jumps to the applications that need a review. */
  onReview: () => void;
}

/**
 * Takes over a KPI slot when an open-to-matching campaign is collecting fresh
 * interest. Deliberately shaped like the surrounding StatCards (label row, value
 * row, hint) so the row stays balanced, but tinted as the one true action card.
 */
export default function ApplicationsNudgeCard({
  count,
  hint,
  onReview,
}: ApplicationsNudgeCardProps) {
  const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onReview();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onReview}
      onKeyDown={handleKey}
      className="group flex h-full flex-col rounded-lg border border-accent-300 bg-accent-50 p-4 text-left transition duration-200 cursor-pointer hover:bg-accent-100 hover:-translate-y-0.5 hover:shadow-md motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2"
    >
      <div className="flex items-start justify-between">
        <span className="flex items-center gap-1.5 text-xs font-label font-medium text-accent-800">
          New applications
          <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" aria-hidden="true" />
        </span>
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-base text-background-50">
          <i className="ri-inbox-archive-line" />
        </span>
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        <span className="font-heading text-2xl font-semibold text-accent-900">{count}</span>
        <span className="mb-0.5 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-label font-semibold text-background-50 transition-transform group-hover:translate-x-0.5">
          Review
          <i className="ri-arrow-right-line" />
        </span>
      </div>
      <p className="mt-1 truncate text-xs text-accent-800">{hint}</p>
    </div>
  );
}